import { Box, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import { useContext, useRef, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { AgentThinking } from "./agent_thinking";
import { AwaitingResponse } from "./awaiting_response";
import { ChatButton } from "./chat_button";
import { SQLDisplay } from "./sql_display";
import { ChartDisplay } from "./chart_display";
import { GenericTableDisplay } from "./generic_table_display";
import { ToolCallsContainer } from "./tool_calls_container";
import { ChatContext } from "../../contexts/chat/context";
import { TableData } from "../../contexts/chat/type";

// Format timestamp as "H:MM AM/PM, Mon DD" in user's timezone
const formatMessageTimestamp = (timestamp: Date): string => {
  const date = new Date(timestamp);
  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12; // 0 should be 12
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const day = date.getDate();
  return `${hours}:${minutes} ${ampm}, ${month} ${day}`;
};

// Process message content to extract buttons and remove them from markdown content
const processMessageContent = (content: string) => {
  const buttonRegex = /\[CHAT_BUTTON:([^:]+):([^:]+):([^\]]+)\]/g;
  const buttons: Array<{ type: string; level: string; label: string }> = [];

  // Remove button syntax from markdown content and collect button data
  const processedContent = content.replace(
    buttonRegex,
    (match, type, level, label) => {
      buttons.push({ type, level, label });
      return ""; // Remove button from markdown content
    },
  );

  return { processedContent, buttons };
};

const DataTable = ({ table }: { table: TableData }) => {
  console.log("Rendering DataTable:", table);

  return (
    <Box sx={{ my: 2 }}>
      {table.name && (
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          {table.name}
        </Typography>
      )}
      <Box sx={{ overflowX: "auto" }}>
        <table
          style={{
            borderCollapse: "collapse",
            width: "100%",
            minWidth: "max-content",
            fontSize: "13px",
          }}
        >
          <thead>
            <tr>
              {table.headers.map((header, i) => (
                <th
                  key={i}
                  style={{
                    border: "1px solid #ddd",
                    padding: "8px",
                    backgroundColor: "#f5f5f5",
                    whiteSpace: "nowrap",
                    textAlign: "left",
                    fontWeight: 600,
                  }}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, i) => (
              <tr key={i}>
                {row.map((cell, j) => (
                  <td
                    key={j}
                    style={{
                      border: "1px solid #ddd",
                      padding: "8px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
      {table.summary && (
        <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
          {table.summary}
        </Typography>
      )}
    </Box>
  );
};

export const MessageContainer = () => {
  const { messages, isAwaitingResponse, sendMessage, agentState } =
    useContext(ChatContext);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const previousMessageCountRef = useRef(0);

  // Debug logging for streaming state
  console.log("MessageContainer RENDER - agentState:", {
    status: agentState.status,
    iteration: agentState.currentIteration,
    isAwaitingResponse,
  });

  // Debug: useEffect to track when agentState changes in this component
  useEffect(() => {
    console.log("MessageContainer useEffect - agentState CHANGED:", {
      status: agentState.status,
      iteration: agentState.currentIteration,
    });
  }, [agentState]);

  // Handle chat button clicks
  const handleButtonClick = (type: string) => {
    console.log(`Button clicked: ${type}`);
    if (type === "CONFIRM") {
      sendMessage("confirm");
    }

    if (type === "DISCARD") {
      sendMessage("discard");
    }
  };

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Scroll only when a new message is appended (not on every streaming state update)
  useEffect(() => {
    if (messages.length > previousMessageCountRef.current) {
      scrollToBottom();
    }
    previousMessageCountRef.current = messages.length;
  }, [messages.length]);

  return (
    <Box sx={{ maxHeight: "100%", overflowY: "auto" }}>
      {messages.map((message, index) => {
        console.log(`Rendering message ${index}:`, {
          content: message.content,
          dataType: message.dataType,
          tablesExist: !!message.tables,
          tablesLength: message.tables?.length,
          firstTable: message.tables?.[0],
        });

        const isTableMessage = message.dataType === "table" && message.tables;
        console.log(`isTableMessage for ${index}:`, isTableMessage);

        const { processedContent, buttons } = isTableMessage
          ? { processedContent: message.content, buttons: [] }
          : processMessageContent(message.content);

        return (
          <Box
            key={index}
            sx={{
              alignSelf: message.type === "sent" ? "flex-end" : "flex-start",
              overflowX: "hidden",
              maxWidth: "96%",
              boxSizing: "border-box",
            }}
          >
            <Box
              sx={{
                m: 1,
                p: 2,
                borderRadius:
                  message.type === "received"
                    ? "4px 16px 16px 16px"
                    : "16px 16px 4px 16px",
                backgroundColor:
                  message.type === "sent" ? "#e6eefb" : "#ffffff",
                borderLeft: message.type === "received" ? "3px solid" : "none",
                borderColor:
                  message.type === "received" ? "primary.main" : "transparent",
                boxShadow:
                  message.type === "received"
                    ? "0 2px 8px rgba(0,0,0,0.06)"
                    : "0 1px 3px rgba(0,0,0,0.08)",
                "& .markdown-content": {
                  "& p": {
                    margin: 0,
                    marginBottom: "0.5em",
                    "&:last-child": {
                      marginBottom: 0,
                    },
                  },
                  "& h1, & h2, & h3, & h4, & h5, & h6": {
                    marginTop: "0.5em",
                    marginBottom: "0.5em",
                    fontWeight: 600,
                    "&:first-child": {
                      marginTop: 0,
                    },
                  },
                  "& h1": { fontSize: "1.5em" },
                  "& h2": { fontSize: "1.3em" },
                  "& h3": { fontSize: "1.1em" },
                  "& ul, & ol": {
                    paddingLeft: "1.5em",
                    margin: "0.5em 0",
                  },
                  "& li": {
                    marginBottom: "0.25em",
                  },
                  "& code": {
                    backgroundColor:
                      message.type === "sent"
                        ? "rgba(255,255,255,0.2)"
                        : "rgba(0,0,0,0.06)",
                    padding: "0.1em 0.3em",
                    borderRadius: "3px",
                    fontSize: "0.9em",
                    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                  },
                  "& pre": {
                    backgroundColor:
                      message.type === "sent"
                        ? "rgba(255,255,255,0.15)"
                        : "rgba(0,0,0,0.04)",
                    padding: "0.75em",
                    borderRadius: "6px",
                    overflow: "auto",
                    margin: "0.5em 0",
                    "& code": {
                      backgroundColor: "transparent",
                      padding: 0,
                      fontSize: "0.85em",
                    },
                  },
                  "& blockquote": {
                    borderLeft: "3px solid",
                    borderColor:
                      message.type === "sent"
                        ? "rgba(255,255,255,0.5)"
                        : "rgba(0,0,0,0.2)",
                    paddingLeft: "1em",
                    marginLeft: 0,
                    marginRight: 0,
                    fontStyle: "italic",
                  },
                  "& table": {
                    borderCollapse: "collapse",
                    boxSizing: "border-box",
                    minWidth: "100%",
                  },
                  "& th, & td": {
                    border: "1px solid",
                    borderColor:
                      message.type === "sent"
                        ? "rgba(255,255,255,0.3)"
                        : "rgba(0,0,0,0.1)",
                    padding: "0.5em",
                    textAlign: "left",
                    fontSize: "12px",
                    whiteSpace: "nowrap",
                  },
                  "& th": {
                    backgroundColor:
                      message.type === "sent"
                        ? "rgba(255,255,255,0.1)"
                        : "rgba(0,0,0,0.03)",
                    fontWeight: 600,
                  },
                  "& strong": {
                    fontWeight: 600,
                  },
                  "& em": {
                    fontStyle: "italic",
                  },
                  "& hr": {
                    border: "none",
                    borderTop: "1px solid",
                    borderColor:
                      message.type === "sent"
                        ? "rgba(255,255,255,0.3)"
                        : "rgba(0,0,0,0.1)",
                    margin: "1em 0",
                  },
                  "& a": {
                    color: message.type === "sent" ? "#87CEEB" : "#0066cc",
                    textDecoration: "underline",
                    "&:hover": {
                      textDecoration: "none",
                    },
                  },
                  "& img": {
                    maxWidth: "100%",
                    height: "auto",
                    borderRadius: "6px",
                  },
                  "& .emoji": {
                    fontSize: "1.2em",
                  },
                },
              }}
            >
              <Box sx={{ overflowX: "auto" }}>
                {isTableMessage && message.content && (
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    {message.content}
                  </Typography>
                )}

                {isTableMessage && message.tables ? (
                  <>
                    {console.log("Rendering tables:", message.tables)}
                    {message.tables.map((table, idx) => (
                      <DataTable key={idx} table={table} />
                    ))}
                  </>
                ) : (
                  <Box className="markdown-content">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        // Custom renderers for better typography
                        p: ({ children }) => (
                          <Typography
                            component="p"
                            variant="body2"
                            sx={{
                              color: "inherit",
                              lineHeight: 1.6,
                            }}
                          >
                            {children}
                          </Typography>
                        ),
                        li: ({ children }) => (
                          <Typography
                            component="li"
                            variant="body2"
                            sx={{
                              color: "inherit",
                              lineHeight: 1.6,
                            }}
                          >
                            {children}
                          </Typography>
                        ),
                        a: ({ children, ...props }: any) => (
                          <Link to={props.href}>{children}</Link>
                        ),
                        // Add emoji support for common ones
                        text: ({ value }: any) => {
                          const emojiMap: { [key: string]: string } = {
                            ":chart:": "📊",
                            ":check:": "✅",
                            ":x:": "❌",
                            ":warning:": "⚠️",
                            ":info:": "ℹ️",
                            ":bulb:": "💡",
                            ":calendar:": "📅",
                            ":clock:": "🕐",
                            ":tag:": "🏷️",
                            ":folder:": "📁",
                            ":bar_chart:": "📊",
                            ":money:": "💰",
                            ":arrow_up:": "⬆️",
                            ":arrow_down:": "⬇️",
                          };

                          let processedValue = value;
                          Object.entries(emojiMap).forEach(([code, emoji]) => {
                            processedValue = processedValue.replace(
                              new RegExp(code, "g"),
                              `<span class="emoji">${emoji}</span>`,
                            );
                          });

                          return (
                            <span
                              dangerouslySetInnerHTML={{
                                __html: processedValue,
                              }}
                            />
                          );
                        },
                      }}
                    >
                      {processedContent}
                    </ReactMarkdown>
                    {buttons.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        {buttons.map((button, buttonIndex) => (
                          <ChatButton
                            key={buttonIndex}
                            label={button.label}
                            level={button.level}
                            type={button.type as "CONFIRM" | "DISCARD"}
                            onButtonClick={handleButtonClick}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                )}

                {/* Display SQL queries if present */}
                {message.type === "received" &&
                  message.sqlQueries &&
                  message.sqlQueries.length > 0 && (
                    <SQLDisplay queries={message.sqlQueries} />
                  )}

                {/* Display charts if present */}
                {message.type === "received" &&
                  message.charts &&
                  message.charts.length > 0 && (
                    <ChartDisplay charts={message.charts} />
                  )}

                {/* Display structured non-SQL tables if present */}
                {message.type === "received" &&
                  message.genericTables &&
                  message.genericTables.length > 0 && (
                    <GenericTableDisplay tables={message.genericTables} />
                  )}

                {message.type === "received" &&
                  message.toolCalls &&
                  message.toolCalls.length > 0 && (
                    <ToolCallsContainer
                      toolCalls={message.toolCalls}
                      isStreaming={false}
                    />
                  )}

                <Typography
                  variant="caption"
                  sx={{ mt: 1, opacity: 0.8, display: "block" }}
                >
                  {formatMessageTimestamp(message.timestamp)}
                </Typography>
              </Box>
            </Box>
          </Box>
        );
      })}
      {/* Show AgentThinking when streaming, AwaitingResponse as fallback */}
      {agentState.status === "streaming" ? (
        <>
          <AgentThinking />
          <ToolCallsContainer
            toolCalls={agentState.toolCalls}
            isStreaming={true}
          />
        </>
      ) : (
        isAwaitingResponse && <AwaitingResponse />
      )}
      <div ref={messagesEndRef} />
    </Box>
  );
};
